import React from 'react';
import { TextSnippetOutlined } from '@mui/icons-material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { getLocale } from '../../utils/dateLocale';

const Logs = ({ logsData }) => {
  const theme = useTheme();
  const { t } = useTranslation('dashboard');

  // Функция для получения цвета статуса
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'info':
        return theme.palette.contrast.green;
      case 'warning':
        return theme.palette.contrast.yellow;
      case 'error':
        return theme.palette.contrast.red;
      default:
        return theme.palette.text.secondary;
    }
  };

  // Нормализация входящих данных логов: поддержка форматов { logs: [] }, { user: [] }, []
  const normalizedLogs = React.useMemo(() => {
    if (Array.isArray(logsData)) return logsData;
    if (Array.isArray(logsData?.logs)) return logsData.logs;
    if (Array.isArray(logsData?.user)) return logsData.user;
    return [];
  }, [logsData]);

  const logsSorted = React.useMemo(() => {
    return normalizedLogs
      .slice()
      .sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0));
  }, [normalizedLogs]);

  const isEmpty = logsSorted.length === 0;

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(getLocale()); } catch { return '-'; }
  };

  const formatAction = (action) => {
    if (!action) return '';
    switch (action) {
      case 'device_command_send':
        return t('logsCommand');
      default:
        return action;
    }
  };

  const formatDetails = (details) => {
    if (!details) return '';
    try {
      // Преобразуем строку в объект, если нужно
      const parsed = typeof details === 'string' ? (() => { try { return JSON.parse(details); } catch { return null; } })() : null;
      const d = parsed || details;

      // Специальное правило форматирования для команд устройств
      if (d && typeof d === 'object' && d.status && d.command) {
        const statusMap = {
          'waiting-confirmation': t('logsAwaitConfirm'),
        };
        const commandMap = {
          'restart': t('logsReboot'),
        };
        const statusLabel = statusMap[d.status] || d.status;
        const commandLabel = commandMap[d.command] || d.command;
        if (commandLabel || statusLabel) {
          return `${commandLabel || ''}${commandLabel && statusLabel ? ' - ' : ''}${statusLabel || ''}`.trim();
        }
      }

      // Показываем кратко ключевые изменения, если есть
      if (Array.isArray(d?.changes) && d.changes.length > 0) {
        const first = d.changes[0];
        const field = first?.field ?? 'field';
        const oldVal = Array.isArray(first?.old) ? first.old.join(',') : first?.old;
        const newVal = Array.isArray(first?.new) ? first.new.join(',') : first?.new;
        return `${field}: ${oldVal ?? '—'} → ${newVal ?? '—'}`;
      }
      // Иначе выводим компактный JSON
      return JSON.stringify(d);
    } catch {
      return '';
    }
  };

  const renderLinkCell = (label, id, type) => {
    if (!label && !id) return '';
    const text = label || id || '';
    if (!id) return text;
    const to = type === 'device' ? `/device/${id}` : `/profile/${id}`;
    return (
      <a
        href={to}
        style={{ color: theme.palette.primary.main, textDecoration: 'underline' }}
        title={to}
      >
        {text}
      </a>
    );
  };

  return (
    <div id="logs-container" style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative' }}>
      <div style={{ position: 'absolute', top: 5, left: 5, display: 'flex', alignItems: 'center', fontSize: '20px', color: theme.palette.text.primary }}>
        <TextSnippetOutlined style={{ marginRight: '5px', color: theme.palette.text.primary }} />
        {t('logsTitle')}
      </div>
      <div style={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: '5px',
        padding: '10px',
        marginTop: '35px',
        height: 'calc(100% - 55px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        border: 'none',
      }}>
        {isEmpty ? (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%',
            color: theme.palette.text.secondary, fontSize: '14px', textAlign: 'center', padding: '20px'
          }}>
            {t('logsEmpty')}
          </div>
        ) : (
          <>
            <TableContainer component={Paper} style={{ boxShadow: 'none', border: 'none', backgroundColor: theme.palette.background.paper }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ color: theme.palette.text.primary, fontSize: '14px', fontWeight: 'bold' }}>{t('logsAction')}</TableCell>
                    <TableCell style={{ color: theme.palette.text.primary, fontSize: '14px', fontWeight: 'bold' }}>{t('logsUser')}</TableCell>
                    <TableCell style={{ color: theme.palette.text.primary, fontSize: '14px', fontWeight: 'bold' }}>{t('logsTarget')}</TableCell>
                    <TableCell style={{ color: theme.palette.text.primary, fontSize: '14px', fontWeight: 'bold' }}>{t('logsDevice')}</TableCell>
                    <TableCell style={{ color: theme.palette.text.primary, fontSize: '14px', fontWeight: 'bold' }}>{t('logsSuccess')}</TableCell>
                    <TableCell style={{ color: theme.palette.text.primary, fontSize: '14px', fontWeight: 'bold' }}>{t('logsDetails')}</TableCell>
                    <TableCell style={{ color: theme.palette.text.primary, fontSize: '14px', fontWeight: 'bold' }}>{t('logsTime')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logsSorted.map((log, idx) => (
                    <TableRow key={log.id || log.log_id || idx}>
                      <TableCell>{formatAction(log.action)}</TableCell>
                      <TableCell>
                        {renderLinkCell(log.requested_by_user_name || log.user_name, log.user_id, 'user')}
                      </TableCell>
                      <TableCell>
                        {renderLinkCell(log.target_user_name, log.target_user_id, 'user')}
                      </TableCell>
                      <TableCell>
                        {renderLinkCell(log.device_model, log.device_id, 'device')}
                      </TableCell>
                      <TableCell>
                        {typeof log.success === 'boolean' ? (
                          <Chip size="small" label={log.success ? 'OK' : 'ERR'} style={{
                            backgroundColor: log.success ? theme.palette.contrast.green : theme.palette.contrast.red,
                            color: '#fff'
                          }} />
                        ) : ''}
                      </TableCell>
                      <TableCell>{formatDetails(log.details || log.error)}</TableCell>
                      <TableCell>{log.created_at ? formatDate(log.created_at) : ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '10px' }}>
              {logsData?.onLoadMore && logsData?.canLoadMore && (
                <Button variant="outlined" size="small" onClick={logsData.onLoadMore} disabled={!!logsData?.loading}>
                  {logsData?.loading ? t('logsLoading') : t('logsLoadMore')}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Logs;
